// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the app shell. Owns the store, wires every action, renders.
//
// ⚠ NOTHING SENSITIVE IS PERSISTED IN THE BROWSER. The access token lives on the
// MobileClient instance, in memory, and dies with the tab; the long-lived device
// credential is an httpOnly cookie this code cannot read. The only thing written
// to localStorage is the address the user typed, so the next visit can skip
// step 1 — a convenience, and no use to anyone who steals it.
// ─────────────────────────────────────────────────────────────────────────────

import { MobileApi, type MobileProfile, type PairingResult } from "home/app/mobile/api";
import { dueFacts } from "home/app/mobile/due";
import {
    assigneeNames,
    MAX_COLLAPSED_GROUPS,
    nudgeResultText,
    parseCollapsedGroups,
    nextGrouping,
    setGroupsCollapsed,
    toggleCollapsedGroup,
} from "home/app/mobile/todos";
import { normaliseTenantHost, stepAfterDiscovery, stepBackFrom, stepForMethod } from "home/app/mobile/onboarding";
import { MobileStore, PAIRING_POLL_MS, PAIRING_TIMEOUT_MS, TOAST_MS, type MobileState } from "home/app/mobile/store";
import { activeTenant, forgetTenant, parseKnownTenants, upsertTenant, type KnownTenant } from "home/app/mobile/tenants";
import { type LifecycleTransition, type LoginMethod, type Priority, type Todo } from "home/app/mobile/types";
import { priorityName } from "home/app/mobile/priorities";
import { relationNames, setRelationNames } from "home/app/mobile/relationNames";
import { type SavedView, viewTenantOf, viewToRestore, viewToSave } from "home/app/mobile/view";
import { MobileApiError, MobileClient } from "home/data/MobileClient";

import { bind, el, fill } from "home/ui/mobile/dom";
import { icon, logo } from "home/ui/mobile/icons";
import { renderOnboarding, type OnboardingActions } from "home/ui/mobile/ui_mobile_onboarding";
import {
    followAccountLanguage,
    initLanguage,
    languageChoice,
    requestedLanguage,
    setLanguageChoice,
} from "home/ui/mobile/ui_mobile_language";
import { currentLanguage, t } from "home/app/mobile/i18n";
import { applyAppearance, readStoredAppearance, writeStoredAppearance } from "home/ui/mobile/ui_mobile_appearance";
import { renderSheet } from "home/ui/mobile/ui_mobile_sheets";
import { initMobileHistory } from "home/ui/mobile/ui_mobile_history";
import { initKeyboard } from "home/ui/mobile/ui_mobile_keyboard";
import { initScrollChrome } from "home/ui/mobile/ui_mobile_scrollchrome";
import {
    clearTodoSearch,
    renderSearchCard,
    renderDetail,
    renderDetailLoading,
    renderHome,
    renderList,
    type TodoActions,
} from "home/ui/mobile/ui_mobile_todos";

/**
 * Where the known installations are remembered. NOT a credential — a host, a
 * name and which method was used, and nothing that grants access to anything.
 * See the header of `app/mobile/tenants.ts`.
 */
const TENANT_KEY = "pqf.mobile.tenant";

function readStoredTenants(): KnownTenant[] {
    try {
        return parseKnownTenants(window.localStorage.getItem(TENANT_KEY));
    } catch {
        // Private mode, or site data blocked. The user types it again; nothing breaks.
        return [];
    }
}

function writeStoredTenants(list: readonly KnownTenant[]): void {
    try {
        window.localStorage.setItem(TENANT_KEY, JSON.stringify(list));
    } catch {
        /* see above */
    }
}

/**
 * Which list groups are folded away. A reading preference for this device, nothing more — so it
 * lives beside the known installations rather than on the server.
 */
const COLLAPSED_KEY = "pqf.mobile.collapsedGroups";

function readCollapsedGroups(): string[] {
    try {
        return parseCollapsedGroups(window.localStorage.getItem(COLLAPSED_KEY));
    } catch {
        return [];
    }
}

function writeCollapsedGroups(keys: readonly string[]): void {
    try {
        window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(keys.slice(-MAX_COLLAPSED_GROUPS)));
    } catch {
        /* private mode: the folding simply does not survive a reload */
    }
}

const RELATION_NAMES_KEY = "pqf.mobile.relationNames";

/** The tenant's role names remembered for `tenant` in `language`, or null for none/another. */
function readRelationNames(tenant: string, language: string): unknown {
    try {
        const saved = JSON.parse(window.localStorage.getItem(RELATION_NAMES_KEY) || "null") as {
            tenant?: unknown;
            language?: unknown;
            names?: unknown;
        } | null;
        return saved && saved.tenant === tenant && saved.language === language ? saved.names : null;
    } catch {
        return null;
    }
}

function writeRelationNames(tenant: string, language: string): void {
    if (!tenant) return;
    try {
        window.localStorage.setItem(RELATION_NAMES_KEY, JSON.stringify({ tenant, language, names: relationNames() }));
    } catch {
        /* private mode: the app's own words until the options answer, on every start */
    }
}

const VIEW_KEY = "pqf.mobile.view";

/** The view this tab left behind, as stored — validated by viewToRestore(), not here. */
function readSavedView(): unknown {
    try {
        return JSON.parse(window.sessionStorage.getItem(VIEW_KEY) || "null");
    } catch {
        return null; // private mode or junk: start on Home, as before
    }
}

function writeSavedView(view: SavedView): void {
    try {
        window.sessionStorage.setItem(VIEW_KEY, JSON.stringify(view));
    } catch {
        /* private mode: a reload starts on Home */
    }
}

/** The one set of view-memory handlers; module-level for the reason the wake handlers are. */
let viewHandler: (() => void) | null = null;

/**
 * Remember the view so a reload comes back to it — see app/mobile/view.ts.
 *
 * Written on every store update that changes the VIEW (cheap: a short JSON string), and the scroll
 * offset on `pagehide` / when the tab is hidden, the last moments a page is sure to get — a scroll
 * listener writing storage on every frame would cost more than it is worth.
 */
function initViewMemory(store: MobileStore): void {
    // The app restores the offset itself once its content has loaded; the browser's own attempt
    // lands on a page that is still empty, and then fights the app's.
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";

    let last = "";
    const save = () => {
        const view = viewToSave(store.getState(), window.scrollY);
        if (!view) return;
        const json = JSON.stringify(view);
        if (json === last) return;
        last = json;
        writeSavedView(view);
    };

    store.subscribe(save);

    if (viewHandler) {
        window.removeEventListener("pagehide", viewHandler);
        document.removeEventListener("visibilitychange", viewHandler);
    }
    viewHandler = save;
    window.addEventListener("pagehide", viewHandler);
    document.addEventListener("visibilitychange", viewHandler);
}

/** The one set of wake handlers; see where they are registered for why they are module-level. */
let wakeHandlers: { visibility: () => void; pageshow: () => void; focus: () => void } | null = null;

export function initUIMobile(root: HTMLElement): void {
    // ⚠ BEFORE anything renders: every string below is read through t() at paint time.
    initLanguage(root);
    const store = new MobileStore();
    const client = new MobileClient({
        baseUrl: root.dataset.apiBase || "api/",
        language: requestedLanguage,
        onSessionLost: () => {
            // ⚠ `sessionExpired`, not a plain sign-out: this person paired before,
            // and the returning screen offers their own method back rather than
            // an empty address field.
            clearTodoSearch();
            store.update({
                searchOpen: false,
                pairingFromApp: false,
                signedIn: false,
                onboarding: "url",
                profile: null,
                todos: [],
                allTodos: null,
                sessionExpired: store.getState().knownTenants.length > 0,
                addingTenant: false,
            });
        },
    });
    const api = new MobileApi(client);

    let toastTimer = 0;
    let pollTimer = 0;
    let pollDeadline = 0;

    const toast = (message: string) => {
        window.clearTimeout(toastTimer);
        store.update({ toast: message });
        toastTimer = window.setTimeout(() => store.update({ toast: null }), TOAST_MS);
    };

    const stopPolling = () => {
        window.clearTimeout(pollTimer);
        pollTimer = 0;
    };

    /**
     * ⚠ A BACKGROUNDED TAB DOES NOT RUN TIMERS, and approval happens in another tab by design.
     *
     * Measured on dev, 2026-08-23: a token was approved at 21:46:01Z and first used successfully at
     * 21:48:45Z — 2m44s later, on a 2-SECOND poll. Nothing was broken; the app tab was frozen the
     * whole time the user was on the tenant's approval page, and woke only when they switched back.
     * The screen meanwhile said "Validating it against …" with no error and no instruction, which
     * is indistinguishable from being stuck — and is what it was reported as, repeatedly.
     *
     * So the poll is driven by RETURNING as well as by the clock: coming back to the tab is the
     * strongest possible signal that something happened on the other one. Registered once for the
     * life of the app and cheap — it only acts while a pairing is actually in flight.
     */
    // ⚠ Replaces any previous app instance's handler rather than adding a second one. initUIMobile()
    // runs once in the browser, but a listener on `document` outlives the closure that registered
    // it — so without this a remount leaves the OLD store still answering the event, polling for a
    // pairing that belongs to a page nobody is looking at.
    if (wakeHandlers) {
        document.removeEventListener("visibilitychange", wakeHandlers.visibility);
        window.removeEventListener("pageshow", wakeHandlers.pageshow);
        window.removeEventListener("focus", wakeHandlers.focus);
    }
    const wake = () => {
        if (document.visibilityState !== "visible") return;
        // ⚠ The condition is that a poll is SCHEDULED, not which screen is showing. The waiting
        // step is "sso" for a tenant method and "login" for a credential one, and enrollmentId
        // outlives a successful pairing — so testing either would wake a loop that has finished,
        // or fail to wake the one that matters. pollTimer is exactly "a pairing is in flight":
        // stopPolling() clears it on success, on cancel and on failure. This accelerates an
        // existing loop; it never starts one.
        if (!pollTimer) return;
        pollNow();
    };

    // ⚠ THREE events, because iOS needs all three and desktop needed none of this.
    //
    // Reported 2026-08-24: pairing completes on a desktop browser and not on an iPhone 12. The
    // handshake is identical, so what differs is the tab lifecycle — Safari on iOS suspends a
    // backgrounded tab far more aggressively than a desktop browser, and restores it from the
    // back/forward cache rather than merely unfreezing it.
    //
    //   visibilitychange — the ordinary tab switch, and the only one desktop needs.
    //   pageshow         — a bfcache RESTORE. The page resumes with its JS state intact and its
    //                      timers still frozen; on iOS this is the usual way back from another
    //                      tab, and it does not reliably fire visibilitychange.
    //   focus            — the standalone (Home Screen) case, where leaving for the approval page
    //                      hands control to Safari and coming back re-focuses the web app.
    //
    // All three route to one handler that does nothing unless a poll is scheduled, so firing two
    // or three times on a single return costs one request at most — pollNow() reschedules rather
    // than stacking, because pollForApproval() clears the pending timer first.
    wakeHandlers = { visibility: wake, pageshow: wake, focus: wake };
    document.addEventListener("visibilitychange", wakeHandlers.visibility);
    window.addEventListener("pageshow", wakeHandlers.pageshow);
    window.addEventListener("focus", wakeHandlers.focus);

    /** The message a failed call should show. `result` is what the user sees. */
    /** Where an open TODO's back leads: the screen it was opened from — see openTodo(). */
    let detailReturn: { tab: MobileState["tab"]; relation: MobileState["relation"]; scrollY: number } | null = null;
    /** The offset to put a screen back at during the one repaint `back()` makes. */
    let pendingScroll: number | null = null;

    const errorText = (error: unknown, fallback: string): string => {
        if (error instanceof MobileApiError) {
            if (error.statusCode === 0)
                return t("mobileShellNoConnection", "No connection. Check your network and try again.");
            return error.message || fallback;
        }
        return fallback;
    };

    /** The status errorText's message came from — null when it was not an HTTP failure at all. */
    const errorStatus = (error: unknown): number | null => (error instanceof MobileApiError ? error.statusCode : null);

    // ── session ──────────────────────────────────────────────────────────────

    /**
     * @param restore the view a reload left behind (see app/mobile/view.ts) — only on the page's own
     *        start, never on a switch of installation or a fresh pairing.
     */
    const loadApp = async (profile: MobileProfile, restore: ReturnType<typeof viewToRestore> = null) => {
        // The ACCOUNT's language from here on. Applied before the update below, whose repaint then
        // draws every screen in it — nothing else needs telling, since t() is read at paint time.
        followAccountLanguage(profile.language, currentLanguage());
        setRelationNames(readRelationNames(viewTenantOf(profile), currentLanguage()));
        store.update({
            signedIn: true,
            pairingFromApp: false,
            profile,
            loading: true,
            loadError: "",
            ...(restore?.patch ?? {}),
        });
        try {
            const todos = await api.todos();
            store.update({ todos, loading: false });
            // A reload on "All TODOs" needs that list before it can tell whether its TODO is there.
            if (restore?.patch.relation === "all") await loadAllTodos();
            // A TODO the reload came back to that is gone (done elsewhere, reassigned) leaves the
            // list on screen; say so in the state too, or the back gesture would "leave" a TODO
            // nobody can see.
            if (restore?.patch.selectedId) {
                const back = store.selected();
                if (!back) store.update({ todoView: "list", selectedId: null });
                else if (!back.permissions || back.partial) completeTodo(back.id);
            }
            // Only now is the screen as long as it was; before, the browser would clamp the offset.
            if (restore && restore.scrollY > 0) window.scrollTo(0, restore.scrollY);
        } catch (error) {
            store.update({
                loading: false,
                loadError: errorText(error, t("mobileShellLoadFailed", "Could not load your TODOs.")),
            });
        }
        // The composer's options are only needed once a sheet opens, so this is
        // deliberately not awaited — a slow project list must not delay the list.
        loadCreateOptions();
    };

    /**
     * The "All TODOs" list — every TODO the tenant lets this person see. Read when the segment is
     * chosen (again on every choice, so it is not a snapshot from the morning), never at start:
     * on a large tenant it is the biggest answer the app asks for.
     */
    const loadAllTodos = async (): Promise<void> => {
        store.update({ allLoading: true, allError: "" });
        try {
            const allTodos = await api.allTodos();
            store.update({ allTodos, allLoading: false });
        } catch (error) {
            store.update({
                allLoading: false,
                allError: errorText(
                    error,
                    t("mobileShellAllTodosFailed", "The list of all TODOs could not be loaded."),
                ),
            });
        }
    };

    /**
     * Read a TODO whole — with what this person may do to it (`permissions`), and for a row from
     * "All TODOs" its moves and activity. A failure on such a row goes back to the list rather than
     * leaving a detail that says "Loading…" for good.
     */
    const completeTodo = (id: string): void => {
        store.update({ todoLoading: id });
        api.todo(id)
            .then((todo) => store.replaceTodo(todo))
            .catch((error) => {
                // A row from "All TODOs" has nothing to show without this read, so it goes back to the
                // list. One of this person's own is whole enough to read and comment on; only its edit
                // controls stay inert, and saying nothing beats a toast on every open while offline.
                if (!store.selected()?.partial) return;
                if (store.getState().selectedId === id) store.update({ todoView: "list" });
                toast(errorText(error, t("mobileShellTodoReadFailed", "This TODO could not be read.")));
            })
            .finally(() => {
                if (store.getState().todoLoading === id) store.update({ todoLoading: null });
            });
    };

    /**
     * The composer's options — and with them the tenant's names for "assignee" and "owner"
     * (relationNames.ts), which are remembered per tenant and language so the next start draws
     * them from its first frame instead of flipping from the app's words once this answers.
     */
    const loadCreateOptions = () => {
        api.createOptions()
            .then((createOptions) => {
                setRelationNames(createOptions.relationNames, createOptions.relationListNames);
                writeRelationNames(viewTenantOf(store.getState().profile), currentLanguage());
                store.update({ createOptions });
            })
            .catch(() => {
                /* the sheets degrade to what they have */
            });
    };

    const bootstrap = async () => {
        // ⚠ THE KNOWN INSTALLATIONS ARE LOADED FIRST, BEFORE ANY SESSION CALL.
        // They are device state and have nothing to do with whether a session
        // exists — and `onSessionLost` reads them to decide whether this is an
        // EXPIRED session or a first run. Loading them afterwards meant that
        // handler always saw an empty list, so a returning user whose token had
        // died was shown the first-run address field instead of their own
        // installation and the method they use.
        const knownTenants = readStoredTenants();
        const current = activeTenant(knownTenants);
        store.update({
            knownTenants,
            tenantInput: current?.url ?? "",
            collapsedGroups: readCollapsedGroups(),
            // Already APPLIED by the bundle's first statement; this is so the account sheet can
            // show which option is chosen.
            appearance: readStoredAppearance(),
            languageChoice: languageChoice(),
        });

        const refreshed = await client.refreshSession();
        if (refreshed) {
            try {
                const profile = await api.me();
                await loadApp(profile, viewToRestore(readSavedView(), viewTenantOf(profile)));
                store.update({ ready: true });
                return;
            } catch {
                // A session that refreshes but cannot answer `me` is not a
                // session — but it IS one that existed, so a returning user gets
                // the recovery card rather than a blank start.
                store.update({ sessionExpired: knownTenants.length > 0 });
            }
        }

        store.update({ ready: true, signedIn: false });
    };

    // ── onboarding actions ───────────────────────────────────────────────────

    const onboardingActions: OnboardingActions = {
        submitTenant(tenantUrl) {
            store.update({ tenantInput: tenantUrl, tenantError: "", onboarding: "discovering" });
            api.discover(tenantUrl)
                .then((discovery) => {
                    const next = stepAfterDiscovery(discovery.methods);
                    store.update({ discovery, onboarding: next });
                    // One method and no choice to make: start it straight away.
                    if (next !== "options" && discovery.methods.length === 1) {
                        onboardingActions.pickMethod(discovery.methods[0]!);
                    }
                })
                .catch((error) => {
                    store.update({
                        onboarding: "failed",
                        onboardingError: errorText(
                            error,
                            t(
                                "mobileShellNoLoginOptions",
                                "We reached {host} but it did not answer with login options.",
                                { host: normaliseTenantHost(tenantUrl) },
                            ),
                        ),
                        onboardingErrorStatus: errorStatus(error),
                    });
                });
        },

        pickMethod(method: LoginMethod) {
            const tenantUrl = store.getState().tenantInput;
            const step = stepForMethod(method);
            // Both kinds normally land on the tenant's own approval page; only a
            // method that says it accepts credentials goes to the form instead.
            store.update({
                onboarding: step,
                onboardingError: "",
                onboardingErrorStatus: null,
                approvalOpened: null,
                lastMethodId: method.id,
            });

            // ⚠ A CREDENTIAL METHOD STARTS NO PAIRING. `auth/start` runs `ApiToken/Create` on the
            // tenant, and an unapproved token can never be deleted — so creating one before anybody
            // has typed a password leaves one behind for every abandoned form. It also opened the
            // approval page in a new tab over the top of the form. With a password, the token is
            // created only after the login has succeeded (docs/mobile-login-plan.md §4.5).
            if (step === "login") return;

            api.startPairing(tenantUrl, method.id)
                .then((pairing) => {
                    store.update({
                        enrollmentId: pairing.enrollmentId,
                        authorizationUrl: pairing.authorizationUrl,
                        authorizationDeadline: pairing.authorizationDeadline,
                    });

                    // ⚠ A NEW TAB, never an iframe: the tenant's first-factor
                    // authority may bounce to the customer's own SAML IdP, and an
                    // IdP sets X-Frame-Options. It would simply render nothing.
                    // ⚠ A BLOCKED POPUP RETURNS null AND DOES NOT THROW. This call sits inside a
                    // promise callback, i.e. outside the user gesture that started it, which Safari
                    // on iOS refuses — while desktop browsers allow it. That difference is the whole
                    // of "it works on my machine and hangs on the phone": no tab opened, so there
                    // was nothing to approve, so the poll waited for an approval that could never
                    // come, forever, with no error. The screen has to say so and offer the link.
                    const opened = window.open(pairing.authorizationUrl, "_blank", "noopener,noreferrer");
                    store.update({ approvalOpened: opened !== null && opened !== undefined });

                    const parsed = Date.parse(pairing.authorizationDeadline);
                    pollDeadline = Number.isNaN(parsed) ? Date.now() + PAIRING_TIMEOUT_MS : parsed;
                    pollForApproval();
                })
                .catch((error) => {
                    store.update({
                        onboarding: "failed",
                        onboardingError: errorText(
                            error,
                            t("mobileShellSignInFailed", "Could not start sign-in with that tenant."),
                        ),
                        onboardingErrorStatus: errorStatus(error),
                    });
                });
        },

        // Reachable only when a method says it accepts credentials — see
        // `LoginMethod.acceptsCredentials` — which no server sends until the sign-in route
        // exists (docs/mobile-login-plan.md §6 step 3). Until then it contacts nothing and says so,
        // rather than finishing a pairing that was never started: it used to call
        // completePairing() on the enrollment `pickMethod()` had created, which is exactly the
        // token-before-password order that step removes.
        signIn(email, password, trustDevice) {
            void email;
            void password;
            void trustDevice;
            store.update({
                onboardingError: t("mobileShellSignInFailed", "Could not start sign-in with that tenant."),
                onboardingErrorStatus: null,
            });
        },

        back() {
            stopPolling();
            store.update({
                onboarding: stepBackFrom(store.getState().onboarding),
                onboardingError: "",
                onboardingErrorStatus: null,
            });
        },

        retry() {
            stopPolling();
            const { tenantInput } = store.getState();
            store.update({ tokenFailedAt: null, tokenStep: 0, onboardingError: "", onboardingErrorStatus: null });
            onboardingActions.submitTenant(tenantInput);
        },

        cancelSso() {
            stopPolling();
            store.update({
                onboarding: "options",
                onboardingError: "",
                onboardingErrorStatus: null,
                enrollmentId: null,
            });
        },

        checkNow() {
            // Deliberately not gated here: pollForApproval() already returns when no pairing is in
            // flight, and duplicating that test is how the two come to disagree.
            pollNow();
        },

        openApp() {
            store.update({ onboarding: "url", tab: "home", sessionExpired: false });
        },

        useTenant(tenant: KnownTenant) {
            // Straight back into discovery for that installation — the method it
            // offers is authoritative, and may have changed since last time.
            store.update({
                tenantInput: tenant.url,
                sessionExpired: false,
                addingTenant: false,
                onboardingError: "",
                onboardingErrorStatus: null,
            });
            onboardingActions.submitTenant(tenant.url);
        },

        /**
         * Back from "add another tenant" to the app. Nothing was paired and nothing was left — the
         * session, the profile and the TODOs are still the current tenant's — so this only puts the
         * app back on screen, on Home.
         */
        backToApp() {
            if (!client.hasSession() || !store.getState().profile) {
                store.update({ pairingFromApp: false });
                return;
            }
            store.update({
                signedIn: true,
                pairingFromApp: false,
                addingTenant: false,
                onboarding: "url",
                tenantInput: "",
                tenantError: "",
                tab: "home",
                todoView: "list",
            });
        },

        addAnother() {
            store.update({ addingTenant: true, tenantInput: "", sessionExpired: false, tenantError: "" });
        },

        forgetTenant(tenant: KnownTenant) {
            const knownTenants = forgetTenant(store.getState().knownTenants, tenant.host);
            writeStoredTenants(knownTenants);
            store.update({ knownTenants, addingTenant: knownTenants.length === 0 });
        },
    };

    /**
     * Make `tenant` the active installation, using the pairing this device already holds.
     *
     * Shared by the account sheet's switch and by the landing after a sign-out, because those are
     * the same operation: both have a live pairing and neither may go back through the tenant's
     * approval page. A 404 means there is no pairing here after all, and the honest answer to that
     * is to start one — the user asked to go there.
     */
    function switchToTenant(tenant: KnownTenant, announce: boolean): void {
        // Up for the WHOLE operation — the switch call and the load behind it — because the screen
        // still holds the other installation's TODOs until the new ones land.
        store.update({
            busyLabel: t("mobileShellSwitching", "Switching to {name}…", { name: tenant.displayName || tenant.host }),
        });
        api.switchTenant(tenant.url)
            .then((result) => {
                client.setAccessToken(result.accessToken);
                // Touching `lastUsed` is what makes it active in the list — which is what the
                // account sheet reads to decide which installation to name.
                const knownTenants = upsertTenant(store.getState().knownTenants, {
                    ...tenant,
                    displayName: result.profile.tenantName ?? tenant.displayName,
                    lastUsed: Date.now(),
                });
                writeStoredTenants(knownTenants);
                // Everything on screen belongs to the installation being left — including a search
                // typed against its rows, which would silently filter the next one's.
                clearTodoSearch();
                store.update({
                    searchOpen: false,
                    pairingFromApp: false,
                    knownTenants,
                    tenantInput: tenant.url,
                    todos: [],
                    selectedId: null,
                    todoView: "list",
                    tab: "home",
                    createOptions: null,
                    allTodos: null,
                    sessionExpired: false,
                    onboarding: "url",
                });
                // ⚠ `finally`, and it must cover the LOAD as well as the switch: clearing it when
                // the switch resolved would drop the overlay onto an empty list for as long as the
                // TODOs take, which is the part that actually looks broken. loadApp() is written
                // not to throw, but an overlay that outlived a rejection would lock the app with
                // no way out but a reload.
                void loadApp(result.profile).finally(() => store.update({ busyLabel: null }));
                if (announce)
                    toast(
                        t("mobileShellSwitched", "Switched to {name}.", {
                            name: result.profile.tenantName || tenant.host,
                        }),
                    );
            })
            .catch((error) => {
                store.update({ busyLabel: null });
                if (error instanceof MobileApiError && error.statusCode === 404) {
                    startPairingWith(tenant.url);
                    return;
                }
                toast(errorText(error, t("mobileShellSwitchFailed", "Could not switch tenant.")));
            });
    }

    /**
     * Into the app, after a pairing has just been approved.
     *
     * ⚠ THE OVERLAY IS FOR THE GAP AFTER THE TOKEN IS VALID. `loadApp()` sets `signedIn` before it
     * fetches anything, so the moment approval lands the render switches to the signed-in screens
     * and paints an EMPTY TODO list — which, at the end of a sign-in somebody has just walked
     * through a browser tab for, reads as "it worked and there is nothing here" rather than as
     * "still loading". Same reason the switch has one, arrived at from the other direction: there
     * the screen holds the previous installation's rows, here it holds none.
     *
     * Both completion paths go through this: the approval POLL and the manual re-check. They had
     * the same three lines twice already, and the overlay would have been the fourth thing to
     * remember to add in both.
     */
    function enterAppAfterPairing(result: PairingResult): void {
        client.setAccessToken(result.accessToken);
        store.update({
            tokenStep: 3,
            tokenFailedAt: null,
            busyLabel: t("mobileShellOpening", "Opening {name}…", {
                name: result.profile.tenantName || result.profile.tenantHost,
            }),
        });
        void loadApp(result.profile)
            // `finally`, so a load that fails still hands the app back rather than leaving the
            // overlay up with no way out but a reload.
            .finally(() => store.update({ onboarding: "done", busyLabel: null }));
    }

    /** Leave the app and pair with an installation. An empty url means an empty address field. */
    function startPairingWith(tenantUrl: string): void {
        store.update({
            signedIn: false,
            sessionExpired: false,
            onboarding: "url",
            tenantInput: tenantUrl,
            // With no url this is the "add another" flow, which must show the field rather than
            // the picker the device would otherwise open on.
            addingTenant: tenantUrl === "",
            tenantError: "",
            onboardingError: "",
            onboardingErrorStatus: null,
        });
        if (tenantUrl) onboardingActions.submitTenant(tenantUrl);
    }

    /** Record the installation that just paired, with whatever we learned about it. */
    function rememberCurrentTenant(displayName?: string): void {
        const state = store.getState();
        const discovery = state.discovery;
        const host = discovery?.host ?? normaliseTenantHost(state.tenantInput);
        const method = discovery?.methods.find((m) => m.id === state.lastMethodId);

        const knownTenants = upsertTenant(state.knownTenants, {
            url: state.tenantInput,
            host,
            displayName: displayName ?? discovery?.displayName,
            methodId: method?.id,
            methodTitle: method?.title,
            lastUsed: Date.now(),
        });
        writeStoredTenants(knownTenants);
        store.update({ knownTenants, sessionExpired: false });
    }

    /**
     * Ask the portal whether the user has approved the token yet.
     *
     * A poll rather than a redirect back, because approval happens on the
     * TENANT's own page — it has no way to call this app back. Bounded by the
     * tenant's own `authorizationDeadline`, so it cannot run forever.
     */
    /** Check immediately rather than after the interval — used on return and by "Check now". */
    function pollNow(): void {
        pollForApproval(0);
    }

    function pollForApproval(delay: number = PAIRING_POLL_MS): void {
        stopPolling();
        const { enrollmentId } = store.getState();
        if (!enrollmentId) return;

        if (Date.now() > pollDeadline) {
            store.update({
                onboarding: "failed",
                onboardingError: t(
                    "mobileShellApprovalExpired",
                    "The approval request expired before it was confirmed. Start again to get a new one.",
                ),
                onboardingErrorStatus: null,
            });
            return;
        }

        pollTimer = window.setTimeout(() => {
            api.completePairing(enrollmentId)
                .then((result) => {
                    stopPolling();
                    rememberCurrentTenant(result.profile.tenantName);
                    store.update({ onboarding: "validating" });
                    enterAppAfterPairing(result);
                })
                .catch((error) => {
                    // ⚠ 409 and ONLY 409 means "not approved yet". 404 used to count too, and
                    // auth/complete returns 404 for "we do not know that address" as well — so an
                    // unresolvable tenant polled silently to its deadline instead of saying so.
                    // Any code that can mean two things cannot be a poll condition.
                    if (error instanceof MobileApiError && error.statusCode === 409) {
                        pollForApproval();
                        return;
                    }
                    stopPolling();
                    store.update({
                        onboarding: "validating",
                        tokenFailedAt: 1,
                        onboardingError: errorText(
                            error,
                            t("mobileShellTokenInvalid", "The token could not be validated against that tenant."),
                        ),
                        onboardingErrorStatus: errorStatus(error),
                    });
                });
        }, delay);
    }

    // ── todo actions ─────────────────────────────────────────────────────────

    const todoActions: TodoActions = {
        openAccount() {
            store.update({ sheet: "account" });
        },

        setAppearance(appearance) {
            applyAppearance(appearance);
            writeStoredAppearance(appearance);
            store.update({ appearance });
        },

        /**
         * The person's own language. The app's words follow at once (t() is read at paint time); the
         * TENANT's words — state names, transitions, the server's sentences — came in the old
         * language, so the TODOs are read again, quietly, in the new one.
         */
        setLanguageChoice(choice) {
            const changed = setLanguageChoice(choice, currentLanguage());
            store.update({ languageChoice: choice });
            if (!changed || !store.getState().signedIn) return;
            // The tenant's names for the two roles come in a language too.
            setRelationNames(readRelationNames(viewTenantOf(store.getState().profile), currentLanguage()));
            loadCreateOptions();
            if (store.getState().allTodos) void loadAllTodos();
            api.todos()
                .then((todos) => store.update({ todos }))
                .catch(() => {
                    /* the list keeps the old words until the next read; nothing is wrong with it */
                });
        },

        /**
         * Switch to an installation this device is already paired with.
         *
         * ⚠ NO RE-APPROVAL on the happy path — the pairing lives in the device cookie and the
         * server only re-orders it. A 404 means that pairing is gone (or was never made on THIS
         * device, since the remembered list is per browser and the credential is per device), and
         * the answer to that is to pair, not to show an error: the user asked to go there.
         */
        switchTenant(tenant) {
            store.update({ sheet: null });
            switchToTenant(tenant, true);
        },

        addInstallation() {
            store.update({ sheet: null });
            startPairingWith("");
            // After startPairingWith(), which is shared with the roads in from outside the app.
            store.update({ pairingFromApp: true });
        },

        /**
         * Sign out of the ACTIVE installation.
         *
         * ⚠ The other pairings survive — the server removes one entry from the device cookie
         * rather than clearing it — so `remaining` decides where the person lands: back in the app
         * on another installation, or on the signed-out picker.
         */
        signOut() {
            store.update({ sheet: null, busyLabel: t("mobileShellSigningOut", "Deleting the access token…") });
            const state = store.getState();
            const leaving = activeTenant(state.knownTenants);

            api.signOut()
                .then((result) => {
                    client.setAccessToken(null);
                    // Forget the installation that was just signed out of; the others stay in the
                    // list, and the next one becomes active by `lastUsed`.
                    const knownTenants = leaving ? state.knownTenants.filter((t) => t.host !== leaving.host) : [];
                    writeStoredTenants(knownTenants);

                    const next = activeTenant(knownTenants);
                    clearTodoSearch();
                    store.update({
                        searchOpen: false,
                        pairingFromApp: false,
                        knownTenants,
                        signedIn: false,
                        profile: null,
                        todos: [],
                        selectedId: null,
                        todoView: "list",
                        tab: "home",
                        createOptions: null,
                        allTodos: null,
                        sessionExpired: false,
                        addingTenant: knownTenants.length === 0,
                        tenantInput: next?.url ?? "",
                        onboarding: "url",
                    });

                    // ⚠ `remaining` is the SERVER's count of live pairings, not the length of the
                    // remembered list — the two can disagree (a device paired in another browser
                    // profile, a list cleared by site-data cleanup), and only the server knows
                    // whether there is a credential to come back on.
                    // Cleared here rather than in a `finally`: when there is somewhere to land,
                    // switchToTenant() puts its own label straight back up, and clearing after it
                    // would flash the app between the two.
                    store.update({ busyLabel: null });

                    if (result.remaining > 0 && next) {
                        // ⚠ SWITCH, never useTenant(). The device is still paired there, and
                        // useTenant() goes back through discovery and the tenant's approval page —
                        // which would make signing out of one installation cost a re-approval on
                        // another one the person never touched.
                        switchToTenant(next, false);
                    }
                })
                .catch((error) => {
                    store.update({ busyLabel: null });
                    toast(errorText(error, t("mobileShellSignOutFailed", "The access token could not be deleted.")));
                });
        },

        openRelation(relation) {
            store.update({ tab: "todos", todoView: "list", relation });
        },
        openTodos() {
            store.update({ tab: "todos", todoView: "list" });
        },
        openTodo(todo) {
            const state = store.getState();
            // Back must lead to exactly the screen the TODO was opened from — Home or the list, its
            // segment, and where it was scrolled. Remembered on the FIRST step into a detail; a TODO
            // opened from another one's search card goes back to where the first came from.
            if (!(state.tab === "todos" && state.todoView === "detail")) {
                detailReturn = { tab: state.tab, relation: state.relation, scrollY: window.scrollY };
            }
            store.update({
                searchOpen: false,
                tab: "todos",
                todoView: "detail",
                selectedId: todo.id,
                // ⚠ The list's segment is NOT changed: it used to switch to the TODO's own relation,
                // so a TODO opened from one list led back to the other.
            });
            // ⚠ READ WHOLE ON EVERY OPEN that does not know yet what this person may do: the lists
            // do not carry `permissions` (one request per row), and the edit controls wait for them.
            // It also completes a row from "All TODOs", and replaces a list row minutes old.
            const opened = store.selected();
            if (opened && (!opened.permissions || opened.partial)) completeTodo(todo.id);
        },
        back() {
            const to = detailReturn;
            detailReturn = null;
            if (!to) {
                store.update({ todoView: "list" });
                return;
            }
            pendingScroll = to.scrollY;
            store.update({ todoView: "list", tab: to.tab, relation: to.relation });
            pendingScroll = null;
        },
        openSearch() {
            store.update({ searchOpen: true });
        },
        closeSearch() {
            store.update({ searchOpen: false });
        },
        setRelation(relation) {
            store.update({ relation });
            if (relation === "all") void loadAllTodos();
        },
        toggleGrouping() {
            store.update({ grouping: nextGrouping(store.getState().grouping) });
        },
        toggleGroup(key: string) {
            const collapsedGroups = toggleCollapsedGroup(store.getState().collapsedGroups, key);
            store.update({ collapsedGroups });
            writeCollapsedGroups(collapsedGroups);
        },
        setGroupsCollapsed(keys: string[], collapse: boolean) {
            const collapsedGroups = setGroupsCollapsed(store.getState().collapsedGroups, keys, collapse);
            store.update({ collapsedGroups });
            writeCollapsedGroups(collapsedGroups);
        },
        openNewSheet(projectId?: string) {
            store.update({ sheet: "new", newTodoProjectId: projectId ?? null });
        },
        openAssigneesSheet() {
            store.update({ sheet: "assignees" });
        },
        openOwnerSheet() {
            store.update({ sheet: "owner", ownerPick: null });
        },
        pickOwner(personId: string) {
            store.update({ ownerPick: personId });
        },

        /**
         * Hand the TODO over to the picked person, who REPLACES the current owner(s) — `PUT
         * todos/{id}/links/owner` with one target. The answer is the TODO as it is now, relations and
         * permissions included, so the screen follows: one handed over from "I own" leaves it.
         */
        handOver() {
            const todo = store.selected();
            const state = store.getState();
            const personId = state.ownerPick;
            if (!todo || !personId || state.assigneeBusy) return;
            const name = state.createOptions?.ownerPeople?.find((p) => p.id === personId)?.name ?? "";
            store.update({ assigneeBusy: personId });
            api.setLinks(todo.id, "owner", [{ id: personId }])
                .then((updated) => {
                    store.replaceTodo(updated);
                    store.update({ sheet: null, ownerPick: null });
                    toast(
                        name
                            ? t("mobileShellHandedOverTo", "Handed over to {name}", { name })
                            : t("mobileShellHandedOver", "Handed over"),
                    );
                })
                .catch((error) => {
                    toast(errorText(error, t("mobileShellHandOverFailed", "The TODO was not handed over.")));
                    // What was written so far stays written (the new owner is added before the old
                    // one is removed), so the screen re-reads rather than trusting what it showed.
                    completeTodo(todo.id);
                })
                .finally(() => store.update({ assigneeBusy: null }));
        },
        openDetailsSheet() {
            store.update({ sheet: "details" });
        },
        openDeleteSheet() {
            store.update({ sheet: "delete" });
        },
        deleteTodo() {
            const todo = store.selected();
            if (!todo) return;
            store.update({ sheet: null, busyLabel: t("mobileShellDeleting", "Deleting…") });
            api.deleteTodo(todo.id)
                .then(() => {
                    // Back first — to where the TODO was opened — then out of the lists.
                    todoActions.back();
                    store.removeTodo(todo.id);
                    toast(t("mobileShellDeleted", "TODO deleted"));
                })
                .catch((error) => toast(errorText(error, t("mobileShellDeleteFailed", "The TODO was not deleted."))))
                .finally(() => store.update({ busyLabel: null }));
        },

        /**
         * Rename, and rewrite the description when the sheet was allowed to edit it.
         *
         * ⚠ `undefined` is not `""`. An omitted description leaves the stored one alone; an empty
         * string clears it. The sheet sends the first for a formatted description it showed
         * read-only, and the route treats the key's absence the same way.
         */
        saveDetails(title: string, description?: string) {
            const todo = store.selected();
            if (!todo) return;
            api.setDetails(todo.id, title, description)
                .then((updated) => {
                    store.replaceTodo(updated);
                    store.update({ sheet: null });
                    toast(t("mobileShellTodoSaved", "Changes saved."));
                })
                // ⚠ The sheet STAYS OPEN on a failure, with what was typed still in its fields —
                // closing it would throw away the only copy of the text. The toast repaints the
                // sheet, which re-enables its Save button and restores the draft (detailsSheet()).
                .catch((error) => toast(errorText(error, t("mobileShellTodoSaveFailed", "The TODO was not changed."))));
        },
        closeSheet() {
            store.update({ sheet: null, transitionDraft: null });
        },

        /** Open the status sheet — nothing picked: the sheet is where the choice is made. */
        openStatusSheet() {
            store.update({
                sheet: "transition",
                transitionDraft: { transition: null, label: "", note: "", busy: false, error: "" },
            });
        },

        /** Open the priority sheet. Owner only; the server refuses anyone else. */
        openPrioritySheet() {
            store.update({ sheet: "priority", priorityPick: null });
        },

        /** Pick a priority in the sheet. Sends nothing — Confirm does. */
        pickPriority(priority: Priority | "") {
            store.update({ priorityPick: priority });
        },

        /**
         * ⚠ Confirmed against the server BEFORE the screen changes.
         *
         * A lifecycle move is not an optimistic update: it may be refused by the
         * tenant's own rules, and the transitions available afterwards are only
         * knowable server-side. Showing it as done and rolling back would mean
         * telling someone their item moved when it did not.
         */
        confirmTransition(transition: LifecycleTransition, label: string, note: string) {
            const state = store.getState();
            const draft = state.transitionDraft;
            const todo = store.selected();
            if (!draft || !todo || draft.busy) return;

            store.update({ transitionDraft: { transition, label, note, busy: true, error: "" } });

            api.fireTransition(todo.id, transition.id, note)
                .then((updated) => {
                    store.update({ sheet: null, transitionDraft: null });
                    store.replaceTodo(updated);
                    toast(t("mobileShellMoved", "Moved to {state}", { state: updated.status }));
                })
                .catch((error) => {
                    const current = store.getState().transitionDraft;
                    if (!current) return;
                    store.update({
                        transitionDraft: {
                            ...current,
                            busy: false,
                            error: errorText(
                                error,
                                t("mobileShellMoveRefused", "That move was refused. Reload and try again."),
                            ),
                        },
                    });
                });
        },

        sendComment(text: string) {
            const todo = store.selected();
            if (!todo) return;
            store.update({ commentBusy: true });
            api.comment(todo.id, text)
                .then((updated) => {
                    store.update({ commentBusy: false });
                    store.replaceTodo(updated);
                    // Every other write on this screen confirms itself. A comment did not, so the
                    // only feedback was the entry appearing in the log — which is exactly what was
                    // NOT happening while the re-read dropped the history.
                    toast(t("mobileShellCommentAdded", "Comment added."));
                })
                .catch((error) => {
                    store.update({ commentBusy: false });
                    toast(errorText(error, t("mobileShellCommentFailed", "Your comment was not saved.")));
                });
        },

        setDueDate(iso: string) {
            const todo = store.selected();
            if (!todo) return;
            api.setDue(todo.id, iso)
                .then((updated) => {
                    store.replaceTodo(updated);
                    // The LABEL, not the raw date: it is the word the screen now shows, and
                    // "Due date set to 2026-09-14" would be the only ISO string in the whole app.
                    toast(
                        iso
                            ? t("mobileShellDueSet", "Due date set to {date}", {
                                  date: dueFacts(updated.dueISO, Date.now()).label,
                              })
                            : t("mobileShellDueCleared", "Due date cleared"),
                    );
                })
                .catch((error) => toast(errorText(error, t("mobileShellDueFailed", "The due date was not changed."))));
        },

        setPriority(priority: Priority | "") {
            const todo = store.selected();
            if (!todo || store.getState().priorityBusy) return;
            store.update({ priorityBusy: true });
            api.setPriority(todo.id, priority)
                .then((updated) => {
                    // The sheet closes on SUCCESS only: a refused change keeps it open, pick intact.
                    store.update({
                        priorityBusy: false,
                        sheet: store.getState().sheet === "priority" ? null : store.getState().sheet,
                    });
                    store.replaceTodo(updated);
                    // Named from the ANSWER, not the request — the server re-reads the TODO and
                    // refuses a change that did not take, so this is what the tenant now holds.
                    toast(
                        updated.priority
                            ? t("mobileShellPrioritySet", "Priority set to {priority}", {
                                  priority: priorityName(updated.priority),
                              })
                            : t("mobileShellPriorityCleared", "Priority cleared"),
                    );
                })
                .catch((error) => {
                    store.update({ priorityBusy: false });
                    toast(errorText(error, t("mobileShellPriorityFailed", "The priority was not changed.")));
                });
        },

        nudge() {
            const todo = store.selected();
            if (!todo) return;
            api.nudge(todo.id)
                // Named from the server's answer — who was emailed, and who was not and why.
                .then((result) => toast(nudgeResultText(todo, result)))
                .catch((error) => toast(errorText(error, t("mobileShellNudgeFailed", "The reminder was not sent."))));
        },

        assign(personId: string) {
            const todo = store.selected();
            if (!todo || store.getState().assigneeBusy) return;
            const name = store.getState().createOptions?.people.find((p) => p.id === personId)?.name ?? "";
            store.update({ assigneeBusy: personId });
            api.assign(todo.id, personId)
                .then((updated) => {
                    store.replaceTodo(updated);
                    toast(
                        name
                            ? t("mobileShellPersonAdded", "{name} added", { name })
                            : t("mobileShellAssigneeAdded", "Assignee added"),
                    );
                })
                .catch((error) => toast(errorText(error, t("mobileShellAssignFailed", "The assignee was not added."))))
                .finally(() => store.update({ assigneeBusy: null }));
        },

        unassign(personId: string) {
            const todo = store.selected();
            if (!todo || store.getState().assigneeBusy) return;
            const name = todo.assignees.find((a) => a.id === personId)?.name ?? "";
            store.update({ assigneeBusy: personId });
            api.unassign(todo.id, personId)
                .then((updated) => {
                    store.replaceTodo(updated);
                    toast(
                        name
                            ? t("mobileShellPersonRemoved", "{name} removed", { name })
                            : t("mobileShellAssigneeRemoved", "Assignee removed"),
                    );
                })
                .catch((error) =>
                    toast(errorText(error, t("mobileShellUnassignFailed", "The assignee was not removed."))),
                )
                .finally(() => store.update({ assigneeBusy: null }));
        },

        createTodo(draft) {
            if (!draft.title) {
                toast(t("mobileShellTitleMissing", "Give the TODO a title first."));
                return;
            }
            // The busy chip from the tap until the new TODO's screen is ready: "Creating…", then
            // "Loading…" while it is read whole, if it has to be.
            store.update({ sheet: null, busyLabel: t("mobileShellCreating", "Creating…") });
            api.create(draft)
                .then((created) => {
                    const state = store.getState();
                    store.update({
                        todos: [...state.todos, created],
                        ...(state.allTodos ? { allTodos: [...state.allTodos, created] } : {}),
                    });
                    // Straight into the new TODO; back leads to where the composer was opened.
                    todoActions.openTodo(created);
                    const names = assigneeNames(created);
                    toast(
                        names
                            ? t("mobileShellCreatedFor", "TODO created for {names}", { names })
                            : t("mobileShellCreated", "TODO created"),
                    );
                })
                .catch((error) => toast(errorText(error, t("mobileShellCreateFailed", "The TODO was not created."))))
                .finally(() => store.update({ busyLabel: null }));
        },
    };

    // ── rendering ────────────────────────────────────────────────────────────

    const tabButton = (label: string, iconName: "house" | "checkSquare", active: boolean, onClick: () => void) => {
        const button = el(
            "button",
            {
                class: "pqm-tab",
                type: "button",
                role: "tab",
                "aria-selected": active ? "true" : "false",
            },
            [
                el("span", { class: "pqm-tab-icon" }, [icon(iconName, 20)]),
                el("span", { class: "pqm-tab-label", text: label }),
            ],
        );
        bind(button, "click", onClick);
        return button;
    };

    /** The overlays on screen after the last paint, by `data-overlay` key. See steadyOverlays(). */
    let shownOverlays = new Set<string>();
    /** Which screen the last paint drew — see screenKeyOf() and keepScreenScroll(). */
    let shownScreen: string | null = null;
    /** Whether the last paint had a sheet or the busy veil up — a new one brings the tab bar back. */
    let shownOverlay = false;
    const scrollChrome = initScrollChrome(root);
    initKeyboard(root);

    const render = (state: MobileState) => {
        if (!state.ready) {
            // The splash while the app finds out who is signed in: the logo, so a slow start reads as
            // the app starting rather than an empty page with a word on it.
            fill(root, [
                el("div", { class: "pqm-splash", role: "status" }, [
                    logo(40),
                    el("div", { class: "pqm-splash-text", text: t("mobileShellLoading", "Mobile client loading…") }),
                ]),
            ]);
            return;
        }

        if (!state.signedIn) {
            renderOnboarding(root, state, onboardingActions);
            return;
        }

        let screen: HTMLElement[];
        if (state.tab === "home") {
            screen = renderHome(state, todoActions);
        } else if (state.todoView === "detail") {
            const todo: Todo | null = store.selected();
            // Not in the lists yet: still loading them (a reload into a detail) — or it is gone.
            screen = todo
                ? renderDetail(todo, state, todoActions)
                : state.loading || (state.relation === "all" && state.allLoading)
                  ? renderDetailLoading(state, todoActions)
                  : renderList(state, todoActions);
        } else {
            screen = renderList(state, todoActions);
        }

        const children: Array<HTMLElement> = [...screen];

        children.push(
            el("div", { class: "pqm-tabbar", role: "tablist" }, [
                tabButton(t("mobileShellTabHome", "Home"), "house", state.tab === "home", () =>
                    store.update({ tab: "home" }),
                ),
                tabButton(
                    t("mobileShellTabTodos", "TODOs"),
                    "checkSquare",
                    state.tab === "todos",
                    todoActions.openTodos,
                ),
            ]),
        );

        if (state.searchOpen) children.push(renderSearchCard(state, todoActions));

        const sheet = renderSheet(state, todoActions);
        if (sheet) children.push(sheet);

        // ⚠ ABOVE THE SHEETS, and it takes the pointer: the screen underneath belongs to the
        // installation being left, and a tap on one of its rows during the switch would act on a
        // system the person has already moved away from. `aria-busy` + `role=status`, so a screen
        // reader is told what is happening rather than just losing its place.
        // The busy chip — for a write like deleting, and while the open TODO is being read whole:
        // the same chip, so loading a TODO looks like what it is, the app waiting on the server.
        const busyText =
            state.busyLabel ??
            (state.tab === "todos" &&
            state.todoView === "detail" &&
            (state.todoLoading !== null || (!store.selected() && (state.loading || state.allLoading)))
                ? t("mobileViewLoading", "Loading…")
                : null);
        if (busyText) {
            children.push(
                el(
                    "div",
                    {
                        class: "pqm-busy",
                        "data-overlay": "busy",
                        role: "status",
                        "aria-live": "polite",
                        "aria-busy": "true",
                    },
                    [
                        el("div", { class: "pqm-busy-card" }, [
                            el("div", { class: "pqm-pulse" }),
                            el("div", { class: "pqm-busy-text", text: busyText }),
                        ]),
                    ],
                ),
            );
        }

        if (state.toast) {
            children.push(
                el("div", {
                    class: "pqm-toast",
                    role: "status",
                    "aria-live": "polite",
                    "data-overlay": `toast:${state.toast}`,
                    text: state.toast,
                }),
            );
        }

        const scroll = sheetScrollOf(root);
        const screenTop = window.scrollY;
        fill(root, children);
        shownOverlays = steadyOverlays(children, shownOverlays, scroll);

        const screenKey = screenKeyOf(state, store.selected()?.id ?? null);
        keepScreenScroll(shownScreen, screenKey, screenTop);
        // Back from a TODO: the screen it was opened from, scrolled where it was.
        if (pendingScroll !== null && screenKey !== shownScreen && window.scrollY !== pendingScroll) {
            window.scrollTo(0, pendingScroll);
        }
        // A new screen, or a sheet arriving, always shows the module menu — and the scroll the
        // restore above just made is the baseline, not a scroll the person made.
        const overlay = Boolean(sheet || state.searchOpen || busyText);
        if (screenKey !== shownScreen || (overlay && !shownOverlay)) scrollChrome.reset();
        shownScreen = screenKey;
        shownOverlay = overlay;
        // A repaint can change how long the page is; the indicator follows.
        scrollChrome.refresh();
    };

    store.subscribe(render);
    initViewMemory(store);
    initMobileHistory(store, {
        closeSheet: todoActions.closeSheet,
        closeSearch: todoActions.closeSearch,
        back: todoActions.back,
    });
    render(store.getState());
    void bootstrap();
}

/**
 * Keep an overlay that was already on screen STILL across a repaint.
 *
 * ⚠ EVERY store update repaints the whole app (`fill(root, …)`), and a repainted sheet, toast or
 * busy veil is a NEW element — so its entry animation (`fade`, `rise`) played again. Adding or
 * removing an assignee repaints three times (the "…" on the button, the answer, the toast), and the
 * sheet slid in afresh each time: the blink. Each overlay carries a `data-overlay` key naming what
 * it is (`sheet:assignees`, `toast:<message>`, `busy`); one whose key was on screen in the previous
 * paint gets `pqm-steady`, which switches its animation off. A new key animates as before, so an
 * overlay still arrives with its motion — it just does not arrive again.
 *
 * Two more things the rebuild cost, fixed here for the same reason: an open sheet's SCROLL position
 * (a long "Add" list jumped back to the top after every tap) and its FOCUS (the scrim was focused on
 * every paint, which is also why only an opening sheet takes focus now).
 *
 * @returns the keys on screen now, for the next paint.
 */
export function steadyOverlays(
    children: HTMLElement[],
    previous: ReadonlySet<string>,
    sheetScroll: { key: string; top: number } | null,
): Set<string> {
    const shown = new Set<string>();

    for (const child of children) {
        const key = child.dataset.overlay;
        if (!key) continue;
        shown.add(key);

        if (previous.has(key)) {
            child.classList.add("pqm-steady");
            if (sheetScroll && sheetScroll.key === key) {
                const panel = child.querySelector<HTMLElement>(".pqm-sheet");
                if (panel) panel.scrollTop = sheetScroll.top;
            }
        } else if (key.startsWith("sheet:")) {
            // Opening: focusable so Escape reaches it before anything inside has focus.
            window.setTimeout(() => child.focus(), 0);
        }
    }

    return shown;
}

/**
 * Which SCREEN a paint draws, for keeping its scroll position — or null for one that has none.
 *
 * ⚠ The same key means "the same content, updated": folding a group, the answer to a write, a
 * toast. A different key means somewhere new, which rightly starts at the top. That is why the
 * list's key carries the RELATION and the GROUPING — "Assigned to me" and "I own", or the list by
 * due date and by project, are different lists, and landing half-way down one because the other
 * was scrolled would be its own bug — and why the detail's carries the TODO's id.
 */
export function screenKeyOf(state: MobileState, selectedId: string | null): string | null {
    if (!state.ready || !state.signedIn) return null;
    if (state.tab === "home") return "home";
    if (state.todoView === "detail" && selectedId) return `detail:${selectedId}`;

    return `list:${state.relation}:${state.grouping}`;
}

/**
 * Put the screen back where it was scrolled, when the repaint drew the same screen — and at the
 * top when it drew a different one.
 *
 * ⚠ The DOCUMENT is the scroller (see ui_mobile_scrollchrome.ts for why), and EVERY store update
 * repaints the whole app: `fill()` empties the root for a moment, the page gets shorter, and the
 * browser clamps the offset. So folding a group halfway down the list, or setting a TODO's priority,
 * threw the reader back to the top. `steadyOverlays()` keeps an open SHEET's scroll for the same
 * reason; this is the screen underneath. Setting it straight after `fill()` is enough: the new body
 * is in the document with its content, and the browser clamps an offset past the end.
 *
 * A different screen starts at the top. With an inner scroller that came free (the body was a new
 * element); the document keeps its offset across screens, so it has to be said.
 */
export function keepScreenScroll(previousKey: string | null, nextKey: string | null, top: number): void {
    if (nextKey === null) return;
    if (previousKey === nextKey) {
        if (window.scrollY !== top) window.scrollTo(0, top);
    } else if (window.scrollY !== 0) {
        window.scrollTo(0, 0);
    }
}

/** The open sheet's key and scroll offset, read before a repaint throws the panel away. */
function sheetScrollOf(root: Element): { key: string; top: number } | null {
    const scrim = root.querySelector<HTMLElement>('[data-overlay^="sheet:"]');
    const panel = scrim?.querySelector<HTMLElement>(".pqm-sheet");

    return scrim && panel ? { key: scrim.dataset.overlay!, top: panel.scrollTop } : null;
}
